import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  fetchPatient,
  updatePatient,
  fetchPatientTreatments,
  fetchPatientPrescriptions,
  fetchPatientLabOrders,
  clearPatient,
} from '../store/patientDetailSlice'
import type { Patient } from '../store/patientDetailSlice'
import { PatientDetailHeader } from '../components/patients/PatientDetailHeader'
import { OverviewTab } from '../components/patients/OverviewTab'
import { TreatmentHistoryTab } from '../components/patients/TreatmentHistoryTab'
import { PrescriptionHistoryTab } from '../components/patients/PrescriptionHistoryTab'
import { LabResultsTab } from '../components/patients/LabResultsTab'

export function PatientDetailPage() {
  const { patientId } = useParams<{ patientId: string }>()
  const dispatch = useAppDispatch()
  const { patient, treatments, prescriptions, labOrders, loading } = useAppSelector(
    (s) => s.patientDetail
  )
  const [activeTab, setActiveTab] = useState('Overview')

  useEffect(() => {
    if (patientId) {
      dispatch(fetchPatient(patientId))
      dispatch(fetchPatientTreatments(patientId))
      dispatch(fetchPatientPrescriptions(patientId))
      dispatch(fetchPatientLabOrders(patientId))
    }
    return () => {
      dispatch(clearPatient())
    }
  }, [dispatch, patientId])

  const handleUpdate = async (data: Partial<Patient>) => {
    if (!patientId) return
    await dispatch(updatePatient({ id: patientId, data }))
  }

  if (loading || !patient) {
    return (
      <div className="p-6 max-sm:p-3" data-testid="patient-detail-page">
        <p className="text-[13px] text-text-muted">Loading patient...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-sm:p-3" data-testid="patient-detail-page">
      <PatientDetailHeader
        patient={patient}
        onUpdate={handleUpdate}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <div className="mt-4">
        {activeTab === 'Overview' && (
          <OverviewTab
            patient={patient}
            treatments={treatments}
            prescriptions={prescriptions}
          />
        )}
        {activeTab === 'Treatment History' && (
          <TreatmentHistoryTab treatments={treatments} />
        )}
        {activeTab === 'Prescription History' && (
          <PrescriptionHistoryTab prescriptions={prescriptions} />
        )}
        {activeTab === 'Lab Results' && (
          <LabResultsTab labOrders={labOrders} />
        )}
      </div>
    </div>
  )
}
